import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import GoalService from '../../services/goalService';
import './Goals.css';

const Goals = () => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: 'other',
    targetValue: '',
    unit: '',
    targetDate: ''
  });
  const [editingGoal, setEditingGoal] = useState(null);

  // Fetch user's goals on component mount
  useEffect(() => {
    fetchGoals();
  }, []);

  // Fetch all goals for current user
  const fetchGoals = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const data = await GoalService.getAllGoals(token);
      
      if (data.success) {
        setGoals(data.goalList);
      } else {
        toast.error(data.message || 'Failed to fetch goals');
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change for new goal form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGoal({
      ...newGoal,
      [name]: value
    });
  };

  // Handle form submission for new goal
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newGoal.title) {
      toast.error('Goal title is required');
      return;
    }
    
    setLoading(true);
    try {
      const data = await GoalService.createGoal(newGoal, token);
      
      if (data.success) {
        toast.success('Goal added successfully');
        setGoals([...goals, data.goalList]);
        // Reset form
        setNewGoal({
          title: '',
          description: '',
          category: 'other',
          targetValue: '',
          unit: '',
          targetDate: ''
        });
      } else {
        toast.error(data.message || 'Failed to add goal');
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Delete a goal
  const handleDelete = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      setLoading(true);
      try {
        const data = await GoalService.deleteGoal(goalId, token);
        
        if (data.success) {
          toast.success('Goal deleted successfully');
          setGoals(goals.filter(goal => goal._id !== goalId));
        } else {
          toast.error(data.message || 'Failed to delete goal');
        }
      } catch (error) {
        console.error('Error deleting goal:', error);
        toast.error('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    }
  };

  // Start editing a goal
  const startEdit = (goal) => {
    setEditingGoal({
      ...goal,
      targetDate: goal.targetDate ? new Date(goal.targetDate).toISOString().split('T')[0] : ''
    });
  };

  // Handle input change for editing goal
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingGoal({
      ...editingGoal,
      [name]: value
    });
  };

  // Update a goal
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!editingGoal.title) {
      toast.error('Goal title is required');
      return;
    }
    
    setLoading(true);
    try {
      const data = await GoalService.updateGoal(editingGoal._id, editingGoal, token);
      
      if (data.success) {
        toast.success('Goal updated successfully');
        setGoals(goals.map(goal => 
          goal._id === editingGoal._id ? data.goal : goal
        ));
        setEditingGoal(null);
      } else {
        toast.error(data.message || 'Failed to update goal');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Update goal progress
  const handleProgressUpdate = async (goalId, currentValue) => {
    setLoading(true);
    try {
      const data = await GoalService.updateProgress(goalId, { currentValue }, token);
      
      if (data.success) {
        toast.success('Progress updated');
        setGoals(goals.map(goal => 
          goal._id === goalId ? data.goal : goal
        ));
      } else {
        toast.error(data.message || 'Failed to update progress');
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingGoal(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="goals-section">
      <h2>Fitness Goals</h2>
      
      {/* Add New Goal Form */}
      <div className="goal-form-card">
        <h3>Add New Goal</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Goal Title*</label>
            <input
              type="text"
              id="title"
              name="title"
              value={newGoal.title}
              onChange={handleInputChange}
              placeholder="e.g., Lose weight, Run 5K"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={newGoal.description}
              onChange={handleInputChange}
              placeholder="Describe your goal..."
              rows="2"
            ></textarea>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={newGoal.category}
                onChange={handleInputChange}
              >
                <option value="weight">Weight</option>
                <option value="strength">Strength</option>
                <option value="endurance">Endurance</option>
                <option value="flexibility">Flexibility</option>
                <option value="nutrition">Nutrition</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="targetDate">Target Date</label>
              <input
                type="date"
                id="targetDate"
                name="targetDate"
                value={newGoal.targetDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="targetValue">Target Value</label>
              <input
                type="number"
                id="targetValue"
                name="targetValue"
                value={newGoal.targetValue}
                onChange={handleInputChange}
                placeholder="e.g., 70"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="unit">Unit</label>
              <input
                type="text"
                id="unit"
                name="unit"
                value={newGoal.unit}
                onChange={handleInputChange}
                placeholder="e.g., kg, minutes"
              />
            </div>
          </div>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Goal'}
          </button>
        </form>
      </div>
      
      {/* Goals List */}
      <div className="goals-list">
        <h3>Your Goals</h3>
        
        {loading && <p className="loading-text">Loading goals...</p>}
        
        {!loading && goals && goals.length === 0 && (
          <p className="empty-state">You haven't set any fitness goals yet. Add one above!</p>
        )}
        
        {goals && Array.isArray(goals) && goals.map(goal => (
          <div key={goal._id} className="goal-card">
            {/* Edit Form (Conditionally Rendered) */}
            {editingGoal && editingGoal._id === goal._id ? (
              <div className="edit-goal-form">
                <h4>Edit Goal</h4>
                <form onSubmit={handleUpdate}>
                  <div className="form-group">
                    <label htmlFor="edit-title">Goal Title*</label>
                    <input
                      type="text"
                      id="edit-title"
                      name="title"
                      value={editingGoal.title}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-description">Description</label>
                    <textarea
                      id="edit-description"
                      name="description"
                      value={editingGoal.description}
                      onChange={handleEditChange}
                      rows="2"
                    ></textarea>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-category">Category</label>
                      <select
                        id="edit-category"
                        name="category"
                        value={editingGoal.category}
                        onChange={handleEditChange}
                      >
                        <option value="weight">Weight</option>
                        <option value="strength">Strength</option>
                        <option value="endurance">Endurance</option>
                        <option value="flexibility">Flexibility</option>
                        <option value="nutrition">Nutrition</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-targetDate">Target Date</label>
                      <input
                        type="date"
                        id="edit-targetDate"
                        name="targetDate"
                        value={editingGoal.targetDate}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-targetValue">Target Value</label>
                      <input
                        type="number"
                        id="edit-targetValue"
                        name="targetValue"
                        value={editingGoal.targetValue}
                        onChange={handleEditChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-unit">Unit</label>
                      <input
                        type="text"
                        id="edit-unit"
                        name="unit"
                        value={editingGoal.unit}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="goal-content">
                <div className="goal-header">
                  <h4>{goal.title}</h4>
                  <span className={`goal-category category-${goal.category}`}>
                    {goal.category.charAt(0).toUpperCase() + goal.category.slice(1)}
                  </span>
                </div>
                
                {goal.description && (
                  <p className="goal-description">{goal.description}</p>
                )}
                
                <div className="goal-details">
                  {goal.targetValue && (
                    <div className="goal-target">
                      <span className="label">Target:</span> 
                      <span className="value">{goal.targetValue} {goal.unit}</span>
                      
                      {goal.currentValue > 0 && (
                        <span className="current-value">
                          Current: {goal.currentValue} {goal.unit}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {goal.targetDate && (
                    <div className="goal-date">
                      <span className="label">Target Date:</span> 
                      <span className="value">{formatDate(goal.targetDate)}</span>
                    </div>
                  )}
                </div>
                
                {/* Progress Bar */}
                {goal.progress !== undefined && (
                  <div className="goal-progress">
                    <div className="progress-label">
                      <span>Progress</span>
                      <span>{goal.progress}%</span>
                    </div>
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar" 
                        style={{width: `${goal.progress}%`}}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Update Progress Form */}
                {goal.targetValue && (
                  <div className="update-progress">
                    <label htmlFor={`progress-${goal._id}`}>Update Progress:</label>
                    <div className="progress-input-row">
                      <input
                        type="number"
                        id={`progress-${goal._id}`}
                        placeholder={`Current value (in ${goal.unit})`}
                        min="0"
                        max={goal.targetValue * 2}
                        defaultValue={goal.currentValue || ''}
                      />
                      <button
                        onClick={() => {
                          const inputEl = document.getElementById(`progress-${goal._id}`);
                          if (inputEl && inputEl.value) {
                            handleProgressUpdate(goal._id, parseFloat(inputEl.value));
                          }
                        }}
                        className="btn btn-small"
                        disabled={loading}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="goal-actions">
                  <button 
                    onClick={() => startEdit(goal)} 
                    className="btn btn-edit"
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(goal._id)} 
                    className="btn btn-delete"
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Goals;
